<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";
import { loadRemote } from "@module-federation/runtime";
import { getAppSettings } from "../config/app-settings";
import { legacyRouterBridge } from "../federation/vue-router-shim";
import { createParentAuthService } from "./parentAuthService";
import { createHostRouterBridge } from "./hostRouterBridge";
import { createPreventNavigationHandler } from "./preventNavigation";
import { createLegacyVuexStore } from "./vuexStore";
import {
  installLegacyShadowStyles,
  registerShadowRoot,
  unregisterShadowRoot,
} from "./shadowStyles";

type LegacyMountFn = (args: Record<string, unknown>) => Promise<(() => void) | void>;

const props = defineProps<{
  /** Catalog id, e.g. "customersUi". */
  remoteId: string;
  /** MF remote name, e.g. "shipbob_customers_ui". */
  remoteName: string;
  /** MF expose path, e.g. "shipbob_customers_ui/AppMount". */
  mountPath: string;
  /**
   * Extra mount() arguments merged over the host defaults: `componentPath`
   * (mount one of the remote's components instead of its App, the way
   * MerchantApp mounts UserManagementUI's AdminBar) and `displayProperties`.
   */
  mountOptions?: Record<string, unknown>;
  /**
   * Render nothing until the remote has mounted, instead of the 200px loading
   * placeholder. For chrome slots (the admin bar) that must not take space
   * before the remote is ready.
   */
  hideLoading?: boolean;
}>();

const emit = defineEmits<{
  /** The remote's mount() resolved and its content is in the page. */
  ready: [];
}>();

const router = useRouter();
const containerRef = ref<HTMLElement>();
const isLoading = ref(true);
const hasError = ref(false);

let unmountFn: (() => void) | null = null;
// The element handed to mount(). Kept outside the template ref: Vue clears the
// ref before onUnmounted runs, and teardown needs the element to find the
// facade(s) that belong to the app mounted inside it.
let mountElement: HTMLElement | null = null;
// Set by onUnmounted. Both awaits in onMounted check it: a route change while
// loadRemote()/mount() is pending must not leave a mounted remote app, grafted
// routes or a beforeunload listener behind with nothing left to clean them up.
let disposed = false;
const navGuard = createPreventNavigationHandler();

function runUnmount(fn: () => void) {
  try {
    fn();
  } catch (error) {
    console.warn(`[LegacyRemoteMount] Error during unmount of "${props.remoteName}":`, error);
  }
}

function teardown() {
  navGuard.uninstall();
  if (mountElement?.shadowRoot) unregisterShadowRoot(mountElement.shadowRoot);
  if (unmountFn) runUnmount(unmountFn);
  unmountFn = null;
  // Drop the facade(s) the bootloader created for the app mounted here. Scoped
  // to this element: the same remote may have another app live elsewhere in
  // the page (UserManagementUI pages + its AdminBar), whose facade and grafted
  // routes must survive this unmount.
  if (mountElement) {
    legacyRouterBridge.disposeMountedIn(props.remoteId, mountElement);
  } else {
    legacyRouterBridge.disposeRemote(props.remoteId);
  }
}

onMounted(async () => {
  if (!containerRef.value) return;
  mountElement = containerRef.value;
  // Lets the bridge tell a facade this bootloader abandons (mount() threw after
  // createRouter()) from one a concurrent bootloader of the same remote is
  // still installing; see disposeMountedIn().
  const endMount = legacyRouterBridge.beginMount(props.remoteId);

  try {
    const settings = getAppSettings();
    const mod = await loadRemote<{ mount: LegacyMountFn }>(props.mountPath);
    if (disposed) return;
    if (!mod?.mount) {
      throw new Error(`[LegacyRemoteMount] No mount() exported from "${props.mountPath}"`);
    }

    navGuard.install();
    // Idempotent: the host's main.ts installs this at bootstrap, but a frame
    // that renders a legacy remote without that bootstrap (a routes-mode
    // remote's harness using the exposed PageFrame and its admin bar) must get
    // the PrimeVue sheet cloning too.
    installLegacyShadowStyles();

    // MerchantApp-era mount args (src/components/micro-front-ends/utils/mfeMixin.js).
    const result = await mod.mount({
      $element: mountElement,
      hostStore: createLegacyVuexStore(),
      hostRouter: createHostRouterBridge(router),
      hostPath: router.currentRoute.value.fullPath,
      parentAuthService: createParentAuthService(),
      standAlone: false,
      host: "merchant-app-host",
      isTelemetryEnabled: settings.telemetry?.enabled ?? false,
      preventNavigationHandler: navGuard.handler,
      formDialog: () => {},
      ...props.mountOptions,
    });

    // No sweep: a remote built with mfeMountWrapperPlugin resolves mount()
    // before its app is mounted (app.mount() waits for the stylesheet onload),
    // so its facade has no root yet and would be taken for an abandoned one.
    endMount({ sweep: false });
    if (typeof result === "function") unmountFn = result;
    // A shadow-DOM remote (mfeMountWrapperPlugin) attached its root inside
    // mount(); PrimeVue's sheets have to be cloned in for it (see shadowStyles).
    if (mountElement.shadowRoot) registerShadowRoot(mountElement.shadowRoot);
    if (disposed) {
      // Unmounted while mount() was pending: onUnmounted ran with nothing to
      // unmount and before the remote grafted its routes, so tear down now.
      teardown();
    } else {
      emit("ready");
    }
  } catch (error) {
    endMount();
    if (!disposed) {
      console.error(`[LegacyRemoteMount] Failed to mount "${props.remoteName}":`, error);
      hasError.value = true;
      // The bootloader may have created a router facade (guards on the host
      // router, a graft holder) before it failed: drop it now rather than at
      // unmount, so a retry does not stack stale facades.
      legacyRouterBridge.disposeMountedIn(props.remoteId, mountElement);
    }
  } finally {
    endMount();
    isLoading.value = false;
  }
});

onUnmounted(() => {
  disposed = true;
  teardown();
});
</script>

<template>
  <div class="legacy-remote-mount">
    <div v-if="isLoading && !hideLoading" class="legacy-remote-mount-loading" aria-busy="true" />
    <div v-else-if="hasError" class="legacy-remote-mount-error">
      Failed to load {{ remoteName }}. Please refresh the page.
    </div>
    <div
      v-show="!isLoading && !hasError"
      ref="containerRef"
      class="legacy-remote-mount-container"
    />
  </div>
</template>

<style scoped>
.legacy-remote-mount,
.legacy-remote-mount-container {
  display: contents;
}

.legacy-remote-mount-loading {
  min-height: 200px;
}

.legacy-remote-mount-error {
  padding: var(--p-spacing-lg);
  color: var(--p-red-500);
}
</style>
